import { useEffect, useRef } from "react";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import type { UtilityMode } from "../types";
import {
  useDiscoverActions,
  useDiscoverContext,
} from "../state/discover-context";
import { useDiscoverConfig } from "../config";

import { Icon } from "@majourneys/icons";
import { PILL_GLINT_STYLE, useProximitySwell, useSlidingPill } from "@majourneys/ui";
interface RailButton {
  readonly mode: UtilityMode;
  readonly icon: string;
  readonly labelKey: string;
  /** Shown instead of the label in the phone bar, where a quarter of the
   *  width cannot hold the full name ("MaJourn…", owner 2026-10-11). */
  readonly shortLabelKey?: string;
  readonly hasUpdate?: boolean;
}

// One outline glyph per mode, at every state. The active item used to swap to
// the `-fill` variant, which `<Icon>` paints with both a fill and a 0.5 stroke:
// on a filled glyph the stroke closes the cut-out, so the compass came out as a
// plain white disc on the navy pill. The mobile bar had already settled this
// the other way for its own reasons — swapping two fills in a single frame
// reads as a flicker rather than a move — and carries one icon whose colour
// changes. The travelling pill is what says "active"; the glyph does not need
// to say it twice. Adding hasUpdate surfaces the amber notification dot.
const RAIL_BUTTONS: readonly RailButton[] = [
  { mode: "explore", icon: "ri-compass-3-line", labelKey: "activity.explore" },
  { mode: "chat", icon: "ri-sparkling-2-line", labelKey: "discover.ai_chat", shortLabelKey: "discover.ai_chat_short" },
  { mode: "trips", icon: "ri-ticket-2-line", labelKey: "discover.trips" },
  { mode: "liked", icon: "ri-heart-3-line", labelKey: "discover.liked" },
];

/**
 * Where a mode lives. Each section is a real path, not a `?utility=` on one
 * address — a guest who wants to send someone the chat needs a link to send.
 * A host that mounts Discover elsewhere overrides this with `config.railHref`.
 */
const MODE_PATH: Record<UtilityMode, string> = {
  explore: "discover",
  cities: "discover",
  chat: "ai-chat",
  trips: "trips",
  liked: "liked",
};

export function defaultRailHref(mode: UtilityMode, lang: string): string {
  return `/${lang}/${MODE_PATH[mode]}`;
}

export function DiscoverRail() {
  const { t, i18n } = useTranslation();
  const { state } = useDiscoverContext();
  const { setUtilityMode } = useDiscoverActions();
  const { railHref } = useDiscoverConfig();
  const lang = i18n.language?.slice(0, 2) || "en";
  const hrefFor = railHref ?? defaultRailHref;
  const buttonsRef = useRef<(HTMLAnchorElement | null)[]>([]);
  const railRef = useRef<HTMLElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);
  const glintRef = useRef<HTMLSpanElement>(null);

  const activeIndex = RAIL_BUTTONS.findIndex(
    (b) => b.mode === state.utilityMode,
  );

  // Look A, "Metal ring" (owner, 2026-10-12): the pill's resting place stays
  // the CSS below, so the server HTML already has it on the right tab; the
  // hook only plays the spring and the glint between two resting places, and
  // swells the bar as a mouse comes near. Down the desktop column it stretches
  // along Y, across the phone row along X.
  // Each section is its own route, so a click remounts the rail a moment
  // later: the handoff key lets the new rail finish the spring the old one
  // started rather than snapping to the tab.
  useSlidingPill({
    index: activeIndex,
    pillRef,
    glintRef,
    indexVar: "--mj-rail-active",
    handoffKey: "discover-rail",
  });
  useProximitySwell(railRef);

  // Tab + ↑/↓ arrow navigation. Arrow keys cycle focus between rail
  // items (not wrapping past the ends) and do NOT activate until the
  // user presses Enter/Space, which keeps keyboard users in control.
  useEffect(() => {
    // Reset the ref array length when the button set changes (it's
    // static here so this just runs once, but it's cheap).
    buttonsRef.current = buttonsRef.current.slice(0, RAIL_BUTTONS.length);
  }, []);

  const handleKey = (event: KeyboardEvent<HTMLAnchorElement>, index: number) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const delta = event.key === "ArrowDown" ? 1 : -1;
      const nextIndex = Math.max(
        0,
        Math.min(RAIL_BUTTONS.length - 1, index + delta),
      );
      buttonsRef.current[nextIndex]?.focus();
    }
  };

  return (
    <aside
      ref={railRef}
      // The rail is a framed panel, so it wears the shared pewter frame
      // rather than a second copy of it: `.mj-dk-frame` paints the band, the
      // fill and the hairline; `.mj-discover-rail` only sets the geometry.
      className="mj-discover-rail mj-dk-frame"
      aria-label={t("discover.explorer_navigation")}
      // Expose the active index for the sliding pill indicator; CSS uses
      // --mj-rail-active to drive it — translateY down the desktop column,
      // translateX across the phone row, from the one number.
      style={{ ["--mj-rail-active" as string]: activeIndex }}
    >
      <div className="mj-discover-rail__track">
        <span
          ref={pillRef}
          className="mj-discover-rail__indicator"
          aria-hidden="true"
          data-none={activeIndex < 0 ? "true" : undefined}
        >
          <span ref={glintRef} style={PILL_GLINT_STYLE} />
        </span>
        {/* Anchors, not buttons. The four sections used to be client state at
            one URL: nothing to crawl, nothing to bookmark, nothing to send
            anyone. They are links now, and a plain left click still switches
            in place — the preventDefault below is what keeps it an app rather
            than four page loads. `aria-current` rather than `aria-pressed`,
            which is a toggle's word and wrong on a link. */}
        {RAIL_BUTTONS.map((button, index) => {
          const isActive = state.utilityMode === button.mode;
          const href = hrefFor(button.mode, lang);
          return (
            <a
              key={button.mode}
              ref={(el) => {
                buttonsRef.current[index] = el;
              }}
              href={href}
              className={`mj-discover-rail__icon${isActive ? " is-active" : ""}`}
              aria-label={t(button.labelKey)}
              aria-current={isActive ? "page" : undefined}
              data-has-update={button.hasUpdate ? "true" : undefined}
              onClick={(event) => {
                // A modified click belongs to the browser: new tab, new
                // window, download. Only the plain one is ours.
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                if (event.button !== 0) return;
                event.preventDefault();
                setUtilityMode(button.mode);
                if (typeof window !== "undefined") {
                  window.history.replaceState(null, "", href);
                }
              }}
              onKeyDown={(event) => handleKey(event, index)}
            >
              <Icon name={button.icon} aria-hidden="true" />
              {button.shortLabelKey ? (
                <span>
                  <span className="mj-discover-rail__label-full">{t(button.labelKey)}</span>
                  <span className="mj-discover-rail__label-short" aria-hidden="true">
                    {t(button.shortLabelKey)}
                  </span>
                </span>
              ) : (
                <span>{t(button.labelKey)}</span>
              )}
            </a>
          );
        })}
      </div>
    </aside>
  );
}
